/**
 * 侧边栏导航组件
 * 小红书风格设计
 */

import React from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useAuthStore } from '../../stores/authStore'
import { supabase } from '../../services/supabase'
import { getUnreadCount } from '../../services/notificationService'
import { ExploreIcon, LabIcon, NotifyIcon, DashboardIcon, UserIcon } from '../common/Icons'
import { BeeLogo } from '../common/BeeLogo'

interface NavItem {
  path: string
  label: string
  icon: React.ReactNode
  showBadge?: boolean
}

const navItems: NavItem[] = [
  { path: '/', label: 'Explore', icon: <ExploreIcon className="nav-icon" /> },
  { path: '/lab', label: 'Agent Lab', icon: <LabIcon className="nav-icon" /> },
  { path: '/notifications', label: 'Notifications', icon: <NotifyIcon className="nav-icon" />, showBadge: true },
  { path: '/dashboard', label: 'Dashboard', icon: <DashboardIcon className="nav-icon" /> },
  { path: '/profile', label: 'Profile', icon: <UserIcon className="nav-icon" /> },
]

// 用户信息类型
interface UserInfo {
  username: string
  avatar_url: string | null
}

const Sidebar: React.FC = () => {
  const location = useLocation()
  const { user, isLoggedIn } = useAuthStore()

  // 获取用户最新信息
  const { data: userInfo } = useQuery<UserInfo>({
    queryKey: ['sidebarUserInfo', user?.id],
    queryFn: async () => {
      if (!user) return { username: '用户', avatar_url: null }
      const { data, error } = await supabase
        .from('users')
        .select('username, avatar_url')
        .eq('id', user.id)
        .single()
      if (error) return { username: user.user_metadata?.name || user.email?.split('@')[0] || 'User', avatar_url: null }
      return data || { username: 'User', avatar_url: null }
    },
    enabled: !!user && isLoggedIn,
    staleTime: 5 * 60 * 1000, // 5分钟缓存
  })

  // 获取未读通知数量
  const { data: unreadCount = 0 } = useQuery({
    queryKey: ['unreadCount'],
    queryFn: async () => {
      if (!user) return 0
      return getUnreadCount(user.id)
    },
    enabled: !!user && isLoggedIn,
    refetchInterval: 60000, // 每分钟刷新
  })

  // User display info
  const displayName = userInfo?.username || user?.user_metadata?.name || user?.email?.split('@')[0] || 'User'
  const displayAvatar = userInfo?.avatar_url || user?.user_metadata?.avatar_url || user?.user_metadata?.picture

  return (
    <nav className="sidebar">
      {/* Logo */}
      <Link to="/" className="logo flex items-center gap-2">
        <BeeLogo showText={true} />
      </Link>

      {/* 导航菜单 */}
      {navItems.map((item) => (
        <Link
          key={item.path}
          to={item.path}
          className={`nav-item ${location.pathname === item.path ? 'active' : ''}`}
        >
          {item.icon}
          <span className="nav-label">{item.label}</span>
          {/* 通知未读数徽章 */}
          {item.showBadge && unreadCount > 0 && (
            <span className="nav-badge">
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          )}
        </Link>
      ))}

      {/* 底部用户信息 */}
      <Link to="/settings" className="user-profile-menu">
        {displayAvatar ? (
          <img
            src={displayAvatar}
            alt="User Avatar"
            className="user-profile-avatar"
          />
        ) : (
          <div className="user-profile-avatar flex items-center justify-center bg-gray-200 text-gray-500 font-medium">
            {displayName.charAt(0).toUpperCase()}
          </div>
        )}
        <div className="user-profile-info">
          <span className="user-profile-name">{displayName}</span>
        </div>
      </Link>
    </nav>
  )
}

export default Sidebar